import React from 'react';
import {
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
    Input,
    Label,
    UncontrolledDropdown
} from 'reactstrap';
import {Telescope} from 'lucide-react';
import Select from 'react-select';
import {useAuthStateManager} from '../../hooks/useAuthStateManager';
import RoleNames from '../../lib/RoleNames';
import {useResourceAllocation} from '../../resource_allocation/context/ResourceAllocationContext.tsx';
import {WhatIfLens} from '../../calc_engine/transform/scenario-projection';
import {TagSelector} from '../utility/TagSelector';
import {Tag, TagScope, TagWithTempId} from '../../types';
import {projectDisplayLabel} from '../../lib/projectCode';

interface ProjectOption {
    value: string;
    label: string;
}

/**
 * Navbar what-if lens picker. Default (null lens) = the published picture:
 * Published + Disclosed + Confirmed projects only.
 *
 * Two mutually exclusive modes (matching applyScenarioToState):
 * - Toggle a switch to widen the lens by a whole category (drafts,
 *   non-disclosed, tentative).
 * - Or pick scenario tags — a named what-if: the published picture plus
 *   exactly the tagged projects. While tags are selected the switches are
 *   ignored (and disabled here); clearing the tags restores them.
 *
 * Plus, in EITHER mode: exclude projects — the cancellation what-if. Excluded
 * projects are dropped from the numbers last, even published or scenario-
 * tagged ones, so "cancel X and win tentative Y" is one lens.
 *
 * Applies across the dashboard, utilisation and every insight. Per-tab
 * (session) by design.
 */
export const WhatIfPicker: React.FC = () => {
    const {isInRole} = useAuthStateManager();
    const {state, whatIfLens, setWhatIfLens} = useResourceAllocation();

    if (!isInRole(RoleNames.CustomerUser)) {
        return null;
    }

    const isActive = whatIfLens !== null;
    const scenarioTagIds = whatIfLens?.scenarioTagIds ?? [];
    const scenarioMode = scenarioTagIds.length > 0;
    const selectedScenarioTags = scenarioTagIds
        .map(id => state.tags[id])
        .filter((tag): tag is Tag => Boolean(tag));
    const excludedProjectIds = whatIfLens?.excludedProjectIds ?? [];

    const projectOptions: ProjectOption[] = Object.values(state.projects)
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(p => ({value: p.id, label: projectDisplayLabel(p)}));
    const excludedOptions: ProjectOption[] = excludedProjectIds.map(id => {
        const project = state.projects[id];
        return {
            value: id,
            label: project ? projectDisplayLabel(project) : 'Unknown project'
        };
    });

    const buildLens = (patch: Partial<WhatIfLens>): WhatIfLens => ({
        includeDraft: whatIfLens?.includeDraft ?? false,
        includeNonDisclosed: whatIfLens?.includeNonDisclosed ?? false,
        includeTentative: whatIfLens?.includeTentative ?? false,
        includeOnHold: whatIfLens?.includeOnHold ?? false,
        includeCancelled: whatIfLens?.includeCancelled ?? false,
        scenarioTagIds,
        excludedProjectIds,
        ...patch
    });

    const applyLens = (next: WhatIfLens) => {
        // Everything off = back to the published picture.
        const isEmpty =
            !next.includeDraft &&
            !next.includeNonDisclosed &&
            !next.includeTentative &&
            !next.includeOnHold &&
            !next.includeCancelled &&
            (next.scenarioTagIds ?? []).length === 0 &&
            (next.excludedProjectIds ?? []).length === 0;
        setWhatIfLens(isEmpty ? null : next);
    };

    const setToggle = (
        key:
            | 'includeDraft'
            | 'includeNonDisclosed'
            | 'includeTentative'
            | 'includeOnHold'
            | 'includeCancelled',
        on: boolean
    ) => {
        applyLens(buildLens({[key]: on}));
    };

    const setScenarioTags = (tags: (Tag | TagWithTempId)[]) => {
        applyLens(
            buildLens({
                scenarioTagIds: tags
                    .map(tag => tag.id)
                    .filter((id): id is string => Boolean(id))
            })
        );
    };

    const setExcludedProjects = (options: readonly ProjectOption[]) => {
        applyLens(
            buildLens({
                excludedProjectIds: options.map(option => option.value)
            })
        );
    };

    return (
        <UncontrolledDropdown nav={false} className="d-inline-flex align-items-center me-2">
            <DropdownToggle
                caret
                size="sm"
                color={isActive ? 'warning' : 'link'}
                className={
                    isActive
                        ? 'd-inline-flex align-items-center gap-1'
                        : 'd-inline-flex align-items-center gap-1 text-light text-decoration-none opacity-75'
                }
                title="Choose what the numbers include"
            >
                <Telescope size={14} />
                {isActive ? 'What-if' : 'Published view'}
            </DropdownToggle>
            {/* Wide enough that real project names survive in the exclusion
                picker without truncating. */}
            <DropdownMenu end style={{minWidth: '420px', maxWidth: '90vw'}}>
                <DropdownItem header>What the numbers include</DropdownItem>
                <DropdownItem text className="small text-muted">
                    Published, disclosed, confirmed projects are always
                    included. Widen the lens to explore what-ifs:
                </DropdownItem>
                <DropdownItem toggle={false} tag="div">
                    <div className="form-check form-switch">
                        <Input
                            type="switch"
                            role="switch"
                            id="whatIfIncludeDraft"
                            checked={whatIfLens?.includeDraft ?? false}
                            disabled={scenarioMode}
                            onChange={e =>
                                setToggle('includeDraft', e.target.checked)
                            }
                        />
                        <Label
                            check
                            for="whatIfIncludeDraft"
                            className={scenarioMode ? 'text-muted' : undefined}
                        >
                            Include draft projects (unpublished)
                        </Label>
                    </div>
                </DropdownItem>
                <DropdownItem toggle={false} tag="div">
                    <div className="form-check form-switch">
                        <Input
                            type="switch"
                            role="switch"
                            id="whatIfIncludeNonDisclosed"
                            checked={whatIfLens?.includeNonDisclosed ?? false}
                            disabled={scenarioMode}
                            onChange={e =>
                                setToggle(
                                    'includeNonDisclosed',
                                    e.target.checked
                                )
                            }
                        />
                        <Label
                            check
                            for="whatIfIncludeNonDisclosed"
                            className={scenarioMode ? 'text-muted' : undefined}
                        >
                            Include non-disclosed projects
                        </Label>
                    </div>
                </DropdownItem>
                <DropdownItem toggle={false} tag="div">
                    <div className="form-check form-switch">
                        <Input
                            type="switch"
                            role="switch"
                            id="whatIfIncludeTentative"
                            checked={whatIfLens?.includeTentative ?? false}
                            disabled={scenarioMode}
                            onChange={e =>
                                setToggle('includeTentative', e.target.checked)
                            }
                        />
                        <Label
                            check
                            for="whatIfIncludeTentative"
                            className={scenarioMode ? 'text-muted' : undefined}
                        >
                            Include tentative projects
                        </Label>
                    </div>
                </DropdownItem>
                <DropdownItem toggle={false} tag="div">
                    <div className="form-check form-switch">
                        <Input
                            type="switch"
                            role="switch"
                            id="whatIfIncludeOnHold"
                            checked={whatIfLens?.includeOnHold ?? false}
                            disabled={scenarioMode}
                            onChange={e =>
                                setToggle('includeOnHold', e.target.checked)
                            }
                        />
                        <Label
                            check
                            for="whatIfIncludeOnHold"
                            className={scenarioMode ? 'text-muted' : undefined}
                        >
                            Include on-hold projects
                        </Label>
                    </div>
                </DropdownItem>
                <DropdownItem toggle={false} tag="div">
                    <div className="form-check form-switch">
                        <Input
                            type="switch"
                            role="switch"
                            id="whatIfIncludeCancelled"
                            checked={whatIfLens?.includeCancelled ?? false}
                            disabled={scenarioMode}
                            onChange={e =>
                                setToggle('includeCancelled', e.target.checked)
                            }
                        />
                        <Label
                            check
                            for="whatIfIncludeCancelled"
                            className={scenarioMode ? 'text-muted' : undefined}
                        >
                            Include cancelled projects
                        </Label>
                    </div>
                </DropdownItem>
                <DropdownItem divider />
                <DropdownItem header>Scenario</DropdownItem>
                <DropdownItem text className="small text-muted">
                    Or pick a scenario by tag: the published picture plus the
                    tagged projects (child tags count).
                    {scenarioMode &&
                        ' The switches above are ignored while a scenario is selected.'}
                </DropdownItem>
                <DropdownItem toggle={false} tag="div">
                    <TagSelector
                        selectedTags={selectedScenarioTags}
                        onChange={setScenarioTags}
                        scope={TagScope.Project}
                        grouped
                        placeholder="Select scenario tags..."
                    />
                </DropdownItem>
                <DropdownItem divider />
                <DropdownItem header>Cancellation</DropdownItem>
                <DropdownItem text className="small text-muted">
                    And in either mode, ask "what if we cancel…": excluded
                    projects drop out of the numbers, even published ones.
                </DropdownItem>
                <DropdownItem toggle={false} tag="div">
                    <Select<ProjectOption, true>
                        isMulti
                        value={excludedOptions}
                        onChange={options =>
                            setExcludedProjects(options ?? [])
                        }
                        options={projectOptions}
                        placeholder="Exclude projects..."
                        isSearchable
                        styles={{menu: styles => ({...styles, zIndex: 9999})}}
                    />
                </DropdownItem>
                <DropdownItem divider />
                <DropdownItem
                    disabled={!isActive}
                    onClick={() => setWhatIfLens(null)}
                >
                    Back to published view
                </DropdownItem>
            </DropdownMenu>
        </UncontrolledDropdown>
    );
};

export default WhatIfPicker;
